/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { omitProps } from '../record/omitProps'
import { parseArgs } from './parseArgs'
import {
  AnyFail,
  IFail,
  TMaybeDetails,
  TDetailsNone,
  TFailArgs,
  TDetails,
  TDetailsFromMaybe,
} from './types'

export class Fail extends Error implements IFail<TDetails, 'Fail'> {
  readonly _tag = 'Fail' as const

  constructor(...args: TFailArgs<TDetails>) {
    const opts = parseArgs<TDetails>(args)
    const details = omitProps(opts, ['message', 'cause'])

    super(opts.message ?? 'An error occured.', { cause: opts.cause })
    Object.assign(this, details)

    Object.setPrototypeOf(this, new.target.prototype)
  }

  toJSON(): TDetails & { _tag: 'Fail' } {
    return { ...this } as any
  }

  toString(): string {
    let output = this.stack ?? `${this.name}: ${this.message}`

    const causeStr = formatCause(this)
    if (typeof causeStr === 'string') output += `\nCaused by: ${causeStr}`

    return output
  }
}

function formatCause(fail: AnyFail): string | null {
  if (fail.cause === undefined) return null

  if (fail.cause instanceof Error)
    return fail.cause.stack ?? fail.cause.toString()
  else if (typeof fail.cause === 'object') {
    try {
      return JSON.stringify(fail.cause, null, 2)
    } catch {
      return String(fail.cause)
    }
  }
  return String(fail.cause)
}

export const TaggedFail = <Tag extends string>(
  tag: Tag,
): new <MaybeDetails extends TMaybeDetails = TDetailsNone>(
  ...args: TFailArgs<MaybeDetails>
) => IFail<MaybeDetails, Tag> & TDetailsFromMaybe<MaybeDetails> => {
  const wrapper = {
    BaseFail: class extends Fail {
      readonly _tag = tag as any
    },
  }

  wrapper.BaseFail.prototype.name = tag
  return wrapper.BaseFail as any
}
